import { useState, useEffect, useRef, memo, forwardRef, useImperativeHandle } from "react";
import { Button } from "./ui/button";
import { Send, Paperclip, X, Mic } from "lucide-react";
import { useCoinGate } from "../contexts/CoinGateContext";

// Isolated ChatInput component to prevent parent re-renders on typing
// Uses CSS variables for theme-aware styling (divine-gold automatically switches between themes)
const ChatInput = memo(forwardRef(({ 
  onSend, 
  loading, 
  placeholder = "Type a message... (Shift+Enter for new line)",
  testId = "chat-input",
  enableImageAttachment = false,
  sessionId = null
}, ref) => {
  const [localInput, setLocalInput] = useState("");
  const { requireCoins } = useCoinGate();
  const [userHadDraft, setUserHadDraft] = useState(false);  // Track if user typed their own text
  const [lastSuggestion, setLastSuggestion] = useState("");  // Track AgentEros suggestion
  const [attachedImage, setAttachedImage] = useState(null);  // { file, base64, preview, mimeType }
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [wantCancel, setWantCancel] = useState(false); // true = slid-left, will cancel on release
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const recStartRef = useRef(0);
  const recCancelRef = useRef(false);
  const startXRef = useRef(0);
  const CANCEL_SLIDE_PX = 80;
  const MAX_REC_SECONDS = 600;
  const MIN_REC_MS = 400;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevLoadingRef = useRef(loading);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    setValue: (value) => {
      // This is called when AgentEros suggestion is used
      // Check if user already had typed something (co-creative input)
      if (localInput.trim()) {
        setUserHadDraft(true);
      }
      setLastSuggestion(value);  // Track the suggestion
      setLocalInput(value);
      // Skip the auto-focus on mobile — sliding the keyboard up when the
      // user taps "Use Suggestion" from the coaching panel is disruptive.
      // Desktop keeps the focus so the caret lands ready to edit.
      if (!isMobileRef.current) {
        setTimeout(() => textareaRef.current?.focus(), 100);
      }
    },
    getValue: () => localInput,
    focus: () => textareaRef.current?.focus(),
    // New methods for co-creative tracking
    getUserHadDraft: () => userHadDraft,
    getLastSuggestion: () => lastSuggestion,
    resetCoCreativeState: () => {
      setUserHadDraft(false);
      setLastSuggestion("");
    },
    clearImage: () => setAttachedImage(null)
  }), [localInput, userHadDraft, lastSuggestion]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [localInput]);

  // Detect mobile once at mount so the focus-related side effects below
  // can be skipped on mobile UAs. On mobile browsers, programmatic .focus()
  // in a non-user-gesture context slides the on-screen keyboard up, which
  // is disruptive on page entry and after every LLM turn.
  const isMobileRef = useRef(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      isMobileRef.current = window.matchMedia("(max-width: 768px)").matches;
    }
  }, []);

  // Auto-focus when loading completes (response received). Desktop only —
  // on mobile this would pop the on-screen keyboard mid-conversation.
  useEffect(() => {
    if (prevLoadingRef.current && !loading && !isMobileRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalInput(newValue);
    // If user is typing (not just receiving a suggestion), mark as having draft
    if (newValue.trim() && newValue !== lastSuggestion) {
      setUserHadDraft(true);
    }
  };

  // Handle image file selection - converts to PNG and compresses for LLM compatibility
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a JPEG, PNG, or WEBP image');
      return;
    }

    // Validate file size (max 20MB before compression)
    if (file.size > 20 * 1024 * 1024) {
      alert('Image must be less than 20MB');
      return;
    }

    // Convert, resize and compress image for LLM API compatibility
    // Target: under 4MB, always output PNG (library hardcodes PNG mime type)
    const compressAndConvert = (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            // Calculate new dimensions if image is too large
            // Max dimension: 1600px for good quality while staying under API limits
            const MAX_DIMENSION = 1600;
            const MAX_SIZE_BYTES = 4 * 1024 * 1024; // 4MB target (API limit is 5MB)
            
            let width = img.width;
            let height = img.height;
            const originalMimeType = file.type || 'image/jpeg';
            
            // Determine output format - keep JPEG for photos, use original format when possible
            const isJpeg = originalMimeType === 'image/jpeg' || originalMimeType === 'image/jpg';
            const outputFormat = isJpeg ? 'image/jpeg' : (originalMimeType === 'image/webp' ? 'image/webp' : 'image/png');
            const quality = isJpeg ? 0.85 : (outputFormat === 'image/webp' ? 0.85 : undefined);
            
            // Check if resize is needed
            const needsResize = width > MAX_DIMENSION || height > MAX_DIMENSION;
            
            // If image is small enough and doesn't need resize, use original
            const originalBase64 = event.target.result.split(',')[1];
            const originalSize = (originalBase64.length * 3) / 4;
            
            if (!needsResize && originalSize <= MAX_SIZE_BYTES) {
              console.log(`Image kept original: ${width}x${height}, ~${(originalSize / 1024 / 1024).toFixed(2)}MB (${originalMimeType})`);
              resolve({
                base64: originalBase64,
                preview: event.target.result,
                mimeType: originalMimeType,
                compressedSize: originalSize
              });
              return;
            }
            
            // Scale down if larger than max dimension
            if (needsResize) {
              if (width > height) {
                height = Math.round((height * MAX_DIMENSION) / width);
                width = MAX_DIMENSION;
              } else {
                width = Math.round((width * MAX_DIMENSION) / height);
                height = MAX_DIMENSION;
              }
            }
            
            // Create canvas and draw resized image
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Output in appropriate format with quality setting for lossy formats
            const dataUrl = canvas.toDataURL(outputFormat, quality);
            const base64 = dataUrl.split(',')[1];
            const estimatedBytes = (base64.length * 3) / 4;
            
            // If still too large, reduce dimensions progressively
            if (estimatedBytes > MAX_SIZE_BYTES) {
              let targetDim = 1024;
              if (estimatedBytes > MAX_SIZE_BYTES * 1.5) {
                targetDim = 800;
              }
              const scale = targetDim / Math.max(width, height);
              canvas.width = Math.round(width * scale);
              canvas.height = Math.round(height * scale);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              const smallerDataUrl = canvas.toDataURL(outputFormat, quality);
              const smallerBase64 = smallerDataUrl.split(',')[1];
              const smallerSize = (smallerBase64.length * 3) / 4;
              
              console.log(`Image resized: ${img.width}x${img.height} -> ${canvas.width}x${canvas.height}, ~${(smallerSize / 1024 / 1024).toFixed(2)}MB (${outputFormat})`);
              
              resolve({
                base64: smallerBase64,
                preview: smallerDataUrl,
                mimeType: outputFormat,
                compressedSize: smallerSize
              });
              return;
            }
            
            console.log(`Image resized: ${img.width}x${img.height} -> ${width}x${height}, ~${(estimatedBytes / 1024 / 1024).toFixed(2)}MB (${outputFormat})`);
            
            resolve({
              base64,
              preview: dataUrl,
              mimeType: outputFormat,
              compressedSize: estimatedBytes
            });
          };
          img.src = event.target.result;
        };
        reader.readAsDataURL(file);
      });
    };

    try {
      const { base64, preview, mimeType, compressedSize } = await compressAndConvert(file);
      
      // Final size check (Anthropic limit is 5MB, we use 4MB for safety)
      if (compressedSize > 4 * 1024 * 1024) {
        alert(`Image is too large (${(compressedSize / 1024 / 1024).toFixed(1)}MB). Maximum is 4MB. Please use a smaller or lower resolution image.`);
        return;
      }
      
      setAttachedImage({
        file,
        base64,
        preview,
        mimeType,
        name: file.name
      });
    } catch (err) {
      console.error('Failed to convert image:', err);
      alert('Failed to process image');
    }

    // Reset file input
    e.target.value = '';
  };

  const removeImage = () => {
    setAttachedImage(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if ((localInput.trim() || attachedImage) && !loading) {
        // Check if message is verbatim suggestion (no edits, no prior draft)
        const usedVerbatim = lastSuggestion && localInput.trim() === lastSuggestion.trim() && !userHadDraft;
        onSend(localInput, { 
          userHadDraft, 
          usedSuggestionVerbatim: usedVerbatim,
          imageBase64: attachedImage?.base64,
          imageMimeType: attachedImage?.mimeType,
          imagePreview: attachedImage?.preview
        });
        setLocalInput("");
        setUserHadDraft(false);
        setLastSuggestion("");
        setAttachedImage(null);
      }
    }
  };

  const handleSend = () => {
    if ((localInput.trim() || attachedImage) && !loading) {
      // Check if message is verbatim suggestion (no edits, no prior draft)
      const usedVerbatim = lastSuggestion && localInput.trim() === lastSuggestion.trim() && !userHadDraft;
      onSend(localInput, { 
        userHadDraft, 
        usedSuggestionVerbatim: usedVerbatim,
        imageBase64: attachedImage?.base64,
        imageMimeType: attachedImage?.mimeType,
        imagePreview: attachedImage?.preview
      });
      setLocalInput("");
      setUserHadDraft(false);
      setLastSuggestion("");
      setAttachedImage(null);
    }
  };

  // ==================== VOICE: hold-to-record ====================
  const stopRecStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
  };

  const startRecording = async (e) => {
    if (loading || isRecording || isTranscribing) return;
    e?.preventDefault?.();
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      import("sonner").then(({ toast }) => toast.error("Voice recording not supported in this browser"));
      return;
    }
    startXRef.current = e?.clientX ?? 0;
    setWantCancel(false);
    // Capture the pointer so the button keeps receiving move/up even if the
    // finger drifts off it. Enables the slide-left-to-cancel gesture.
    try { e?.currentTarget?.setPointerCapture?.(e.pointerId); } catch (_) {}
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      recCancelRef.current = false;

      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;

      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stopRecStream();
        const heldMs = Date.now() - recStartRef.current;
        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        setIsRecording(false);
        setWantCancel(false);
        if (recCancelRef.current || heldMs < MIN_REC_MS || chunks.length === 0) {
          return;
        }
        setIsTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          const fd = new FormData();
          const ext = (blob.type.split(";")[0].split("/")[1]) || "webm";
          fd.append("file", blob, `voice.${ext}`);
          fd.append("duration_s", String(Math.round(heldMs / 100) / 10));
          if (sessionId) fd.append("session_id", sessionId);
          const { authAxios } = await import("../utils/auth");
          if (!(await requireCoins())) {
            setIsTranscribing(false);
            return;
          }
          const resp = await authAxios.post("/voice/transcribe", fd, { timeout: 300000 });
          const text = (resp?.data?.text || "").trim();
          if (!text) {
            const { toast } = await import("sonner");
            toast.info("Didn't catch that — try again a bit closer to the mic");
            return;
          }
          setLocalInput((prev) => (prev?.trim() ? `${prev.trim()} ${text}` : text));
          setUserHadDraft(true);
          setTimeout(() => {
            const merged = (textareaRef.current?.value || text).trim();
            if (!merged) return;
            onSend(merged, {
              userHadDraft: true,
              usedSuggestionVerbatim: false,
              imageBase64: attachedImage?.base64,
              imageMimeType: attachedImage?.mimeType,
              imagePreview: attachedImage?.preview,
              source: "voice",
            });
            setLocalInput("");
            setUserHadDraft(false);
            setLastSuggestion("");
            setAttachedImage(null);
          }, 50);
        } catch (err) {
          const { toast } = await import("sonner");
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Transcription failed");
        } finally {
          setIsTranscribing(false);
        }
      };

      rec.start();
      recStartRef.current = Date.now();
      setIsRecording(true);
      // Safety cap — auto-stop at MAX_REC_SECONDS
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          try { mediaRecorderRef.current.stop(); } catch (_) {}
        }
      }, MAX_REC_SECONDS * 1000);
    } catch (err) {
      import("sonner").then(({ toast }) => {
        toast.error(err?.name === "NotAllowedError"
          ? "Microphone permission denied — enable it in your browser settings"
          : "Could not access microphone");
      });
      stopRecStream();
      setIsRecording(false);
    }
  };

  const trackSlide = (e) => {
    if (!isRecording) return;
    const dx = (e.clientX ?? 0) - startXRef.current;
    // Slid in EITHER direction past the threshold → will cancel on release
    if (Math.abs(dx) >= CANCEL_SLIDE_PX && !wantCancel) setWantCancel(true);
    else if (Math.abs(dx) < CANCEL_SLIDE_PX && wantCancel) setWantCancel(false);
  };

  const stopRecording = (e, opts = {}) => {
    e?.preventDefault?.();
    if (!isRecording) return;
    if (opts.cancel || wantCancel) recCancelRef.current = true;
    try { e?.currentTarget?.releasePointerCapture?.(e.pointerId); } catch (_) {}
    if (mediaRecorderRef.current?.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    } else {
      stopRecStream();
      setIsRecording(false);
      setWantCancel(false);
    }
  };

  useEffect(() => () => stopRecStream(), []);

  return (
    <div className="flex flex-col gap-2">
      {/* Image Preview */}
      {attachedImage && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-divine-gold/30">
          <img 
            src={attachedImage.preview} 
            alt="Attached" 
            className="h-16 w-16 object-cover rounded"
          />
          <span className="flex-1 text-sm text-white/70 truncate">{attachedImage.name}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={removeImage}
            className="h-8 w-8 p-0 text-white/50 hover:text-white hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      {/* Text Input */}
      <textarea
        ref={textareaRef}
        data-testid={testId}
        value={localInput}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={2}
        className="w-full px-4 py-3 rounded-xl resize-none overflow-y-auto bg-white/5 border border-divine-gold/30 text-white placeholder-white/30 focus:border-divine-gold focus:ring-1 focus:ring-divine-gold/30"
        style={{ minHeight: "80px", maxHeight: "200px" }}
        disabled={loading}
      />
      
      {/* Bottom Row: Paperclip + Mic + Send */}
      <div className="relative flex items-center justify-between">
        {/* Floating pop-up above the mic button while recording */}
        {(isRecording || isTranscribing) && (
          <div
            className="pointer-events-none absolute right-[80px] bottom-[52px] z-10"
            data-testid="voice-record-hint"
          >
            <div
              className={`px-3 py-1.5 rounded-lg text-xs font-mono shadow-lg border whitespace-nowrap ${
                isTranscribing
                  ? "bg-black/90 border-white/20 text-white/70"
                  : wantCancel
                    ? "bg-black/90 border-red-500/70 text-red-300 font-bold uppercase tracking-wider"
                    : "bg-black/90 border-red-500/50 text-red-300 animate-pulse"
              }`}
            >
              {isTranscribing
                ? "Transcribing…"
                : wantCancel
                  ? "CANCEL"
                  : "◀── slide to cancel ──▶"}
            </div>
            {/* little arrow pointing down at the button */}
            <div
              className={`w-2 h-2 rotate-45 -mt-1 ml-auto mr-4 border-r border-b ${
                isTranscribing
                  ? "bg-black/90 border-white/20"
                  : wantCancel
                    ? "bg-black/90 border-red-500/70"
                    : "bg-black/90 border-red-500/50"
              }`}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          {enableImageAttachment && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageSelect}
                className="hidden"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || isRecording || isTranscribing}
                title="Attach image"
                data-testid="attach-image-btn"
                className="h-[44px] px-3 mr-2 bg-white/5 hover:bg-white/10 text-white/70 border border-divine-gold/20"
              >
                <Paperclip className="w-5 h-5" />
              </Button>
            </>
          )}
        </div>
        <Button
          data-testid="voice-record-btn"
          type="button"
          onPointerDown={startRecording}
          onPointerMove={trackSlide}
          onPointerUp={(e) => stopRecording(e)}
          onPointerCancel={(e) => stopRecording(e, { cancel: true })}
          onContextMenu={(e) => e.preventDefault()}
          disabled={loading || isTranscribing}
          title={isRecording ? (wantCancel ? "Release to cancel" : "Release to send • slide left to cancel") : "Hold to record voice"}
          className={`h-[44px] px-3 mr-2 select-none touch-none transition-colors ${
            isRecording
              ? wantCancel
                ? "bg-red-600 hover:bg-red-600 text-white ring-2 ring-red-400"
                : "bg-red-600 hover:bg-red-600 text-white animate-pulse ring-2 ring-red-400"
              : "bg-white/5 hover:bg-white/10 text-white/70 border border-divine-gold/20"
          }`}
        >
          {isTranscribing ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : wantCancel ? (
            <X className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </Button>
        <Button
          data-testid="send-btn"
          onClick={handleSend}
          disabled={loading || isRecording || isTranscribing || (!localInput.trim() && !attachedImage)}
          className="h-[44px] px-4 bg-divine-gold hover:bg-divine-gold-light text-black"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </Button>
      </div>
    </div>
  );
}));

ChatInput.displayName = "ChatInput";

export default ChatInput;
